import type {
  MemberLevelItemDiscount,
  MemberLevelItemMergeDTO,
  MemberLevelItemPrice,
  MemberLevelScope,
} from "@/api/product/types/product"
import type { MerchantCustomerUserDetailDTO } from "@/api/user/types/member"

type MemberLevelIdentity = Pick<
  MerchantCustomerUserDetailDTO,
  "merchantLevelId" | "brandMemberLevelId" | "enabledMemberPrice"
>

interface ScopedLevelRow {
  levelScope?: MemberLevelScope | null
}

type ResolvedMemberBenefitSnapshot = {
  type?: string | null
  memberDiscount?: Pick<MemberLevelItemDiscount, "discount"> | null
  memberPrice?: Pick<MemberLevelItemPrice, "memberPrice"> | null
}

export const resolveEditableLevelScope = (
  configured: ScopedLevelRow | null | undefined,
  fallbackScope: MemberLevelScope | null | undefined,
) => configured?.levelScope ?? fallbackScope

const hasId = (value: number | null | undefined): value is number => value !== null && value !== undefined

const findScopedLevelRow = <T extends ScopedLevelRow>(
  rows: T[] | null | undefined,
  getLevelId: (row: T) => number | null | undefined,
  member: MemberLevelIdentity | null | undefined,
): T | undefined => {
  if (!member) return undefined
  const source = (rows ?? []).filter((row): row is T => row != null)
  const explicit = source.find((row) => {
    const levelId = getLevelId(row)
    if (row.levelScope === "SHOP") return hasId(member.merchantLevelId) && levelId === member.merchantLevelId
    if (row.levelScope === "BRAND") return hasId(member.brandMemberLevelId) && levelId === member.brandMemberLevelId
    return false
  })
  if (explicit) return explicit

  const legacyShop = source.find(row => row.levelScope == null
    && hasId(member.merchantLevelId)
    && getLevelId(row) === member.merchantLevelId)
  if (legacyShop) return legacyShop

  return source.find(row => row.levelScope == null
    && hasId(member.brandMemberLevelId)
    && getLevelId(row) === member.brandMemberLevelId)
}

export const findMemberDiscount = (
  config: MemberLevelItemMergeDTO | null | undefined,
  member: MemberLevelIdentity | null | undefined,
): MemberLevelItemDiscount | undefined => findScopedLevelRow(
  config?.memberDiscount,
  row => row.merchantMemberLevelId,
  member,
)

export const findMemberPrice = (
  config: MemberLevelItemMergeDTO | null | undefined,
  member: MemberLevelIdentity | null | undefined,
): MemberLevelItemPrice | undefined => findScopedLevelRow(
  config?.memberPrice,
  row => row.memberLevelId,
  member,
)

export const resolveMemberLevelDiscountInfo = (
  config: MemberLevelItemMergeDTO | null | undefined,
  member: MemberLevelIdentity | null | undefined,
  memberPriceEnabled: boolean | number | null | undefined = member?.enabledMemberPrice,
) => {
  if (!config || !member) return null
  const memberDiscount = config.type === "memberDiscount"
    ? findMemberDiscount(config, member)
    : undefined
  const memberPrice = config.type === "memberPrice" && (memberPriceEnabled === true || memberPriceEnabled === 1)
    ? findMemberPrice(config, member)
    : undefined
  return {
    type: memberDiscount ? "memberDiscount" : memberPrice ? "memberPrice" : "noDiscount",
    memberDiscount,
    memberPrice,
  }
}

/**
 * 按后端冻结快照的口径计算整行商品净额。
 *
 * 会员折扣必须先按「单价 × 数量」得到整行原价，再向下取整；不能先取整单价后再乘数量，
 * 否则奇数分金额和多数量商品会与服务端成交金额相差 1 分。
 */
export const resolveMemberBenefitNetAmount = (
  grossAmount: number,
  count: number,
  snapshot: ResolvedMemberBenefitSnapshot | null | undefined,
): number | null => {
  if (!snapshot) return null
  if (snapshot.type === "memberDiscount") {
    const configuredDiscount = snapshot.memberDiscount?.discount
    if (configuredDiscount == null) return null
    const discount = Number(configuredDiscount)
    return Number.isFinite(discount) ? multiplyAmountByDecimalRate(grossAmount, discount) : null
  }
  if (snapshot.type === "memberPrice") {
    const configuredPrice = snapshot.memberPrice?.memberPrice
    if (configuredPrice == null) return null
    const memberPrice = Number(configuredPrice)
    return Number.isFinite(memberPrice) ? memberPrice * count : null
  }
  return null
}

function multiplyAmountByDecimalRate(amount: number, rate: number) {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(String(rate))
  if (match) {
    const decimals = match[2] ?? ""
    const scale = 10 ** decimals.length
    const numerator = Number(`${match[1]}${decimals}`)
    const scaledAmount = amount * numerator
    if (Number.isSafeInteger(scaledAmount) && Number.isSafeInteger(scale)) {
      return Math.floor(scaledAmount / scale)
    }
  }
  return Math.floor(amount * rate)
}

type EditableLevelDefinition = Pick<MemberLevelItemDiscount, "merchantMemberLevelId" | "levelScope">

const isRepresentableRow = (
  levelId: number | null | undefined,
  levelScope: MemberLevelScope | null | undefined,
  levels: EditableLevelDefinition[],
  currentScope: MemberLevelScope,
) => {
  // 连锁下的 null scope 无法仅凭数值 ID 判断原本属于门店还是品牌；即使数值碰撞也必须提示确认。
  if (currentScope === "BRAND" && levelScope == null) return false
  return levels.some(level => level.merchantMemberLevelId === levelId
    && (levelScope == null || levelScope === currentScope))
}

export const hasUnrepresentableMemberBenefitRows = (
  config: MemberLevelItemMergeDTO | null | undefined,
  levels: EditableLevelDefinition[] | null | undefined,
  currentScope: MemberLevelScope,
) => {
  const definitions = levels ?? []
  return (config?.memberDiscount ?? []).some(row => row == null || !isRepresentableRow(
    row.merchantMemberLevelId,
    row.levelScope,
    definitions,
    currentScope,
  )) || (config?.memberPrice ?? []).some(row => row == null || !isRepresentableRow(
    row.memberLevelId,
    row.levelScope,
    definitions,
    currentScope,
  ))
}

export const preserveOriginalMemberBenefitWhenUntouched = <TOriginal, TEdited>(
  original: TOriginal,
  edited: TEdited,
  touched: boolean,
): TOriginal | TEdited => touched ? edited : original
